import { useEffect, useState, type ReactNode } from 'react';
import { UserRound } from 'lucide-react';
import { API_BASE } from '../state/client';
import { useApp } from '../state/AppContext';
import { bundledImage } from '../state/staticImages';
import {
  loadMemberAvatar,
  resolveAvatarImage,
  subscribeAvatars,
  type RemoteAvatarImage,
} from '../state/avatar';
/** Shared account photos and bundled demo portraits never depend on browser storage. */
export function AvatarImage({
  userId,
  source,
  mediaId,
  probe = false,
  size = 40,
}: {
  userId?: string;
  source?: string;
  mediaId?: string;
  /** When true, try live delivery even before avatar_media_id is known. */
  probe?: boolean;
  fallback: ReactNode;
  size?: number;
}) {
  const { mode, session } = useApp();
  const [revision, setRevision] = useState(0);
  const [remote, setRemote] = useState<RemoteAvatarImage | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const remoteMediaId = mediaId || (probe ? '' : null);
  useEffect(
    () =>
      subscribeAvatars(() => {
        setRevision((value) => value + 1);
        setFailed(null);
      }),
    [],
  );
  useEffect(() => {
    setRemote(null);
    if (
      mode !== 'live' ||
      !userId ||
      remoteMediaId === null ||
      !/^[a-f\d]{8}-(?:[a-f\d]{4}-){3}[a-f\d]{12}$/i.test(userId)
    )
      return;
    const controller = new AbortController();
    let objectUrl: string | null = null;
    void (async () => {
      const blob = await loadMemberAvatar(
        API_BASE,
        userId,
        session?.access_token,
        controller.signal,
        fetch,
        `${remoteMediaId}:${revision}`,
      );
      if (!blob || controller.signal.aborted) return;
      objectUrl = URL.createObjectURL(blob);
      setRemote({
        url: objectUrl,
        userId,
        mediaId: remoteMediaId,
        token: session?.access_token,
        revision,
      });
    })().catch(() => {});
    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [mode, session?.access_token, userId, remoteMediaId, revision]);
  const image = resolveAvatarImage({
    live: mode === 'live',
    userId,
    mediaId,
    probe,
    token: session?.access_token,
    revision,
    source,
    remote,
  });
  const bundled = bundledImage(image);
  return image && image !== failed ? (
    <img
      src={bundled?.src || image}
      srcSet={bundled?.srcSet}
      sizes={bundled ? `${size}px` : undefined}
      width={bundled?.width}
      height={bundled?.height}
      alt=""
      decoding="async"
      referrerPolicy="no-referrer"
      onError={() => setFailed(image)}
      style={{
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        borderRadius: 'inherit',
        display: 'block',
      }}
    />
  ) : (
    <UserRound aria-hidden="true" size={Math.round(size * 0.55)} color="white" />
  );
}
